import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", nationality: "", passport: "", travelers: 1, notes: "" };

export default function Clients() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/clients");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/clients/${id}`);
    load();
  };

  const filtered = items.filter(
    (c) => !q || [c.name, c.phone, c.email, c.passport].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label">{t("clients")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("clients")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={openNew} data-testid="client-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("newClient")}
        </Button>
      </div>

      <div className="dashboard-card">
        <div className="flex items-center gap-2 mb-4 max-w-sm">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="ps-9" data-testid="client-search-input" />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("nationality")}</TableHead>
                <TableHead>{t("passport")}</TableHead>
                <TableHead>{t("travelers")}</TableHead>
                <TableHead className="text-end">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>{c.email || "—"}</TableCell>
                  <TableCell>{c.nationality || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.passport || "—"}</TableCell>
                  <TableCell>{c.travelers}</TableCell>
                  <TableCell className="text-end space-x-1 rtl:space-x-reverse">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}>
                      <Pencil size={15} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(c.id)} className="text-red-600">
                      <Trash2 size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? t("editClient") : t("addClient")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" /></Field>
            <Field label={t("phone")}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></Field>
            <Field label={t("email")}><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label={t("nationality")}><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></Field>
            <Field label={t("passport")}><Input value={form.passport} onChange={(e) => setForm({ ...form, passport: e.target.value })} /></Field>
            <Field label={t("travelers")}><Input type="number" min={1} value={form.travelers} onChange={(e) => setForm({ ...form, travelers: parseInt(e.target.value || "1") })} /></Field>
            <div className="sm:col-span-2">
              <Field label={t("notes")}><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="client-save-btn" className="bg-[#0A192F] hover:bg-[#112240] text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
